import React, { useEffect, useState, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../config/firebaseConfig";
import CoursesPage from "./mainpages/coursepages/CoursesPage";
import ClassListsPage from "./mainpages/classlistspage/ClassListsPage";
import UserManagementPage from "./mainpages/usermanagementpage/UserManagementPage";
import MyCourses from "./mainpages/coursepages/StudentCoursesPage";
import NotFound from "./mainpages/NotFound";
import CourseDetailPage from "./mainpages/coursepages/CourseDetailPage";
import ModuleDetailPage from "./mainpages/coursepages/moduledetailpage/ModuleDetailPage"; // Import the new component
import Header from "./Header";
import StudentModuleView from "./mainpages/coursepages/moduledetailpage/StudentModuleView";
import HomePage from "./mainpages/HomePage";
import GradebookPage from "./mainpages/gradebookpage/GradebookPage";
import ProfilePage from "./mainpages/profilepage/ProfilePage";
import AITutorPage from "./mainpages/tutorpage/AITutorPage";
import StudentTutorPage from "./mainpages/tutorpage/StudentTutorPage";
import TutorConversationsList from "./mainpages/tutorpage/TutorConversationsList";
import StudentProfilePage from "./mainpages/gradebookpage/StudentProfilePage";
import IncidentManagementPage from "./mainpages/incidentpage/IncidentManagementPage";

const getPageTitle = (path) => {
  switch (path) {
    case "/legal":
      return "Legal Information";
    case "/":
      return "Home";
    case "/home":
      return "Home";
    case "/gradebook":
      return "Gradebook";
    case "/my-courses":
      return "My Courses";
    case "/courses":
      return "Courses";
    case "/class-lists":
      return "Class Lists";
    case "/user-management":
      return "User Management";
    case "/ai-tutors":
      return "AI Tutors";
    default:
      if (path.startsWith("/courses/")) {
        return path.includes("/modules/") ? "Module Details" : "Course Builder";
      }
      return "My Courses";
  }
};

const UserView = () => {
  const [userRole, setUserRole] = useState(null);
  const [accessiblePages, setAccessiblePages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  const [, setPageTitle] = useState(getPageTitle(location.pathname));

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const [getUserRole, getAccessiblePages] = [
          httpsCallable(functions, "getUserRole"),
          httpsCallable(functions, "getAccessiblePages"),
        ];

        const [
          {
            data: { role },
          },
          {
            data: { pages },
          },
        ] = await Promise.all([getUserRole(), getAccessiblePages()]);

        console.log("Accessible pages:", pages);
        setUserRole(role);
        setAccessiblePages(pages);
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="user-view">
      <Header accessiblePages={accessiblePages} />
      <div className="main-content">
        <Routes>
          <Route
            path="/my-courses"
            element={<MyCourses setPageTitle={setPageTitle} />}
          />
          <Route
            path="/sview/courses/:courseId/sections/:sectionId/modules/:moduleId/student/:userId"
            element={<StudentModuleView setPageTitle={setPageTitle} />}
          />
          {accessiblePages.includes("courses") && (
            <>
              <Route
                path="/courses"
                element={<CoursesPage setPageTitle={setPageTitle} />}
              />
              <Route
                path="/courses/:courseId"
                element={<CourseDetailPage setPageTitle={setPageTitle} />}
              />
              <Route
                path="/courses/:courseId/sections/:sectionId/modules/:moduleId"
                element={<ModuleDetailPage setPageTitle={setPageTitle} />}
              />
            </>
          )}
          {accessiblePages.includes("class-lists") && (
            <Route
              path="/class-lists"
              element={<ClassListsPage setPageTitle={setPageTitle} />}
            />
          )}
          {accessiblePages.includes("user-management") && (
            <Route
              path="/user-management"
              element={<UserManagementPage setPageTitle={setPageTitle} />}
            />
          )}
          <Route
            path="/home"
            element={<HomePage setPageTitle={setPageTitle} />}
          />
          <Route
            path="/gradebook"
            element={
              accessiblePages.includes("gradebook") ? (
                <GradebookPage setPageTitle={setPageTitle} />
              ) : (
                <Navigate to="/home" replace />
              )
            }
          />
          <Route
            path="/gradebook/courses/:courseId"
            element={
              accessiblePages.includes("gradebook") ? (
                <GradebookPage setPageTitle={setPageTitle} />
              ) : (
                <Navigate to="/home" replace />
              )
            }
          />
          <Route
            path="/gradebook/courses/:courseId/modules/:moduleId"
            element={
              accessiblePages.includes("gradebook") ? (
                <GradebookPage setPageTitle={setPageTitle} />
              ) : (
                <Navigate to="/home" replace />
              )
            }
          />
          <Route
            path="/gradebook/students/:studentId"
            element={
              accessiblePages.includes("gradebook") ? (
                <StudentProfilePage setPageTitle={setPageTitle} />
              ) : (
                <Navigate to="/home" replace />
              )
            }
          />
          {accessiblePages.includes("ai-tutors") && (
            <Route
              path="/ai-tutors"
              element={<AITutorPage setPageTitle={setPageTitle} />}
            />
          )}
          <Route
            path="/ai-tutors/:tutorId"
            element={<StudentTutorPage setPageTitle={setPageTitle} />}
          />
          <Route
            path="/ai-tutors/:tutorId/conversations/:conversationId"
            element={<StudentTutorPage setPageTitle={setPageTitle} />}
          />
          <Route
            path="/my-tutor-conversations"
            element={<TutorConversationsList setPageTitle={setPageTitle} />}
          />
          {accessiblePages.includes("incident-management") && (
            <Route
              path="/incident-management"
              element={<IncidentManagementPage setPageTitle={setPageTitle} />}
            />
          )}
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="*" element={<NotFound />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Routes>
      </div>
    </div>
  );
};

export default UserView;
